import { useEffect, useState, type FormEvent } from "react";
import { LANGS, useI18n } from "../i18n";
import { initials } from "../lib/fleet";
import { navigate, type Route } from "../router/hash";
import { useSession } from "../state/session";
import { Icon } from "./Icon";

export type Crumb = { label: string; href?: string };

export function TopBar({
  crumbs,
  route,
  alarmCount,
  onOpenMenu,
  menuOpen,
}: {
  crumbs: Crumb[];
  route: Route;
  alarmCount: number;
  onOpenMenu: () => void;
  menuOpen: boolean;
}) {
  const { t, lang, setLang } = useI18n();
  const { user, provider } = useSession();
  const routeQuery = route.name === "vehicles" ? (route.query.get("q") ?? "") : "";
  const [q, setQ] = useState(routeQuery);

  useEffect(() => setQ(routeQuery), [routeQuery]);

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    const site = route.name === "vehicles" ? (route.query.get("site") ?? undefined) : undefined;
    const type = route.name === "vehicles" ? (route.query.get("type") ?? undefined) : undefined;
    navigate("/vehicles", { q: q.trim() || undefined, site, type });
  };

  const alarmLabel = alarmCount > 0 ? t("topbar.alarmsCount", { count: alarmCount }) : t("topbar.noAlarms");

  return (
    <header className="topbar">
      <button
        type="button"
        className="icon-button menu-button"
        onClick={onOpenMenu}
        aria-expanded={menuOpen}
        aria-controls="rail"
        title={t("nav.openMenu")}
      >
        <Icon name="menu" size={20} label={t("nav.openMenu")} />
      </button>
      <img src="/brand/expartise-logo.png" alt={t("app.logoAlt")} className="topbar-logo" width={104} height={33} />

      <nav className="breadcrumb" aria-label={t("topbar.breadcrumb")}>
        <ol>
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1;
            return (
              <li key={i} className={last ? "is-current" : undefined}>
                {c.href && !last ? <a href={c.href}>{c.label}</a> : <span aria-current={last ? "page" : undefined}>{c.label}</span>}
                {!last && <Icon name="chevronRight" size={14} className="breadcrumb-sep" />}
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="topbar-actions">
        {provider.mode === "demo" && <span className="demo-chip">{t("app.demoBadge")}</span>}
        <form role="search" className="search" onSubmit={onSearch}>
          <Icon name="search" size={16} className="search-icon" />
          <label htmlFor="global-search" className="sr-only">
            {t("topbar.searchLabel")}
          </label>
          <input
            id="global-search"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("topbar.searchPlaceholder")}
            autoComplete="off"
          />
        </form>
        <div className="lang-switch" role="group" aria-label={t("topbar.language")}>
          {LANGS.map((l) => (
            <button
              key={l}
              type="button"
              aria-pressed={lang === l}
              className={lang === l ? "is-on" : undefined}
              onClick={() => {
                if (l === lang) return;
                setLang(l);
                // E-mails follow the console language (profiles.locale); the UI works even if this fails.
                if (user) void provider.updateLocale(l).catch(() => undefined);
              }}
              title={l === "tr" ? t("lang.trName") : t("lang.enName")}
            >
              {l === "tr" ? t("lang.tr") : t("lang.en")}
            </button>
          ))}
        </div>
        <a href="#/alarms" className="icon-button bell" aria-label={`${t("topbar.alarms")}: ${alarmLabel}`} title={alarmLabel}>
          <Icon name="bell" size={19} />
          {alarmCount > 0 && <span className="bell-badge mono">{alarmCount > 99 ? "99+" : alarmCount}</span>}
        </a>
        {user && (
          <a href="#/profile" className="avatar" title={t("topbar.account", { name: user.displayName })} aria-label={t("topbar.account", { name: user.displayName })}>
            {initials(user.displayName)}
          </a>
        )}
      </div>
    </header>
  );
}
